import {Component, OnInit, ViewChild, OnDestroy, Renderer2} from '@angular/core';
import {PersonsService} from '../../../../../services/persons.service';
import {FeedLogService} from '../../../../../services/feedLog.service';
import * as moment from 'moment';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {ActivityTrackerService} from '../../../../../services/activityTracker.service';
import ActivityTrackerModel from '../../../../../services/activityTracker.model';
import {ExcelService} from 'src/app/services/excelService';
import {DatePipe, LocationStrategy} from '@angular/common';
import * as _ from 'lodash';
import {GoogleMap, MapInfoWindow, MapMarker} from '@angular/google-maps';
import {ToastrService} from 'ngx-toastr';
import {CommonMethodsService} from 'src/app/services/commonMethods.service';
import {PreviousRouteService} from 'src/app/services/previousRouteService';
import PersonsModel from "../../../../../services/persons.model";

/**
 * @component ActivityTrackerComponent
 * 
 * @description
 * Comprehensive activity tracking and reporting component for aquaculture farm technicians.
 * This component provides real-time location tracking, activity logging, and analytics visualization
 * for provider technicians, farm managers, and administrators. It integrates Google Maps for location
 * visualization, charts for analytics, and advanced filtering capabilities to monitor technician activities
 * across multiple farms and business entities.
 * 
 * Key Features:
 * 1. **Role-Based Access Control**: Supports EMPLOYEE, Business Admin (Provider), and Provider Manager roles with
 *    specific data filtering for each role
 * 2. **Technician Activity Tracking**: Monitors 7 activity types (Feed Log, Observation Log, Treatment Log, Sampling,
 *    Harvest Log, Readings, OnBoarding) with location and timestamp data
 * 3. **Geographic Visualization**: Google Maps integration with custom markers showing technician visit locations,
 *    punch-in/out times, and farm locations
 * 4. **Lead Management Analytics**: Tracks leads with conversion metrics including Shrimp vs Fish culture ratio,
 *    total area coverage, and lead conversion tracking
 * 5. **Advanced Filtering**: Multi-criteria filtering by technician, date range, and leads/activities with combined
 *    filter orchestration
 * 6. **Dual Tab Interface**: "All Activities" tab for comprehensive activity tracking and "Leads" tab for lead
 *    conversion analytics
 * 7. **Data Export**: Excel export functionality for lead records and analytics
 * 8. **Fullscreen Map View**: Responsive fullscreen map experience with automatic dimension adjustment
 * 9. **Duplicate Activity Handling**: Smart deduplication of activities for same date/time at same location
 * 
 * Form Structure:
 * - trackerSearchForm: Contains selectTechnician (required) and nested dateRangeField (start/end dates required)
 * - dateRangeForm: Independent date range form with start and end date defaults to today
 * 
 * Role-Specific Behavior:
 * - **EMPLOYEE**: Can view all provider technicians in the system
 * - **Business Admin (Provider)**: Can view all technicians assigned to their business
 * - **Provider Manager**: Can view only technicians reporting to them
 * 
 * Google Maps Integration:
 * - Custom pin icon (MapsPinIcon.svg) for location markers
 * - Dynamic zoom adjustment (max 20 levels) with auto-fit bounds on data load
 * - Offset-based duplicate location handling to prevent marker overlap
 * - Info window display with visit timestamps and farm details
 * 
 * Data Processing Pipeline:
 * 1. User selects technician and date range → searchData() initiated
 * 2. getActivityDate() fetches raw activity records from backend
 * 3. Activity type specific parsers extract location and timing data (getFeedLogData, getObservationLog, etc.)
 * 4. Single technician → arrangeSingleTechnicianTableData() with deduplication
 * 5. Multiple technicians → arrangeMultipleTechnicianTableData() with combined results
 * 6. Tab display logic: Activities only → All Activities tab, Leads only → Leads tab, Both → All Activities tab
 * 7. Google Maps initialization → leads filtering → export ready
 * 
 * @selector app-activity-tracker
 * @implements OnInit, OnDestroy
 * @lifecycle ngOnInit, ngAfterViewInit, ngOnDestroy
 * 
 * Dependencies and Injection Strategy:
 * 1. ToastrService: Toast notifications for user feedback
 * 2. FormBuilder: Reactive form creation for search and date range
 * 3. PersonsService: Technician data loading and HTTP request management
 * 4. FeedLogService: Activity data fetching from backend
 * 5. ActivityTrackerService: Activity-specific tracking functionality
 * 6. ExcelService: Data export to Excel format
 * 7. DatePipe: Date formatting for display (dd-MMM-yyyy format)
 * 8. CommonMethodsService: Shared utility methods and error handling
 * 9. LocationStrategy: Navigation history tracking
 * 10. PreviousRouteService: Route history management
 * 11. Renderer2: DOM manipulation for fullscreen map display and styling
 * 
 * @example
 * // Component initialization flow
 * ngOnInit() → Load technicians based on role → Filter by business/reporting structure
 * 
 * // Search and visualization flow
 * searchData() → getActivityDate() → Parse activities → arrangeTechnicianTableData() 
 * → initializeGoogleMaps() → Tab display
 * 
 * // Leads analytics
 * leadsTableData() → arrangeLeadsTableData() → Calculate metrics (ratio, conversions, area)
 * 
 * @see PersonsModel - User/Technician data structure
 * @see ActivityTrackerModel - Activity tracking model
 * @see FeedLogService - Activity data API
 */
@Component({
  selector: 'app-activity-tracker',
  templateUrl: './activity-tracker.component.html',
  styleUrls: ['./activity-tracker.component.scss']
})
export class ActivityTrackerComponent implements OnInit, OnDestroy {
  // ============================================================================
  // PROPERTIES DOCUMENTATION
  // ============================================================================
  
  /**
   * @type {ActivityTrackerModel}
   * @description Location tracking details for the current activity being viewed
   * @default null
   */
  public locationDetails: ActivityTrackerModel;

  /**
   * @type {GoogleMap}
   * @description ViewChild reference to Google Map component for map operations
   * @see https://angular.google-maps.dev/overview
   * @default null
   */
  @ViewChild('gm', {static: false}) mapElement!: GoogleMap;

  /**
   * @type {MapInfoWindow}
   * @description ViewChild reference to Google Map Info Window for displaying location details on marker hover
   * @default null
   */
  @ViewChild(MapInfoWindow) infoWindow!: MapInfoWindow;

  /**
   * @type {boolean}
   * @description Flag tracking fullscreen mode state for responsive map dimension adjustment
   * @default false
   * @side-effect Used to set map height between 100% (fullscreen) and 360px (normal)
   */
  private isFullScreen = false;

  /**
   * @type {FormGroup}
   * @description Main reactive form for activity tracker search containing:
   * - selectTechnician: Required dropdown for technician selection
   * - dateRangeField: Nested FormGroup with start and end date requirements
   * @default Created in constructor with Validators.required
   * @validation selectTechnician is required; both date range fields required
   */
  trackerSearchForm: FormGroup;

  /**
   * @type {Array}
   * @description All persons fetched based on login user's role before filtering to only technicians
   * @default []
   * @side-effect Filtered to extract only persons with 'Provider Technician' role for techniciansData population
   */
  promiseResults = new Array();

  /**
   * @type {boolean}
   * @description Flag to always show calendar in date picker UI
   * @default true
   */
  alwaysShowCalendars: boolean;

  /**
   * @type {moment.Moment}
   * @description Maximum selectable date in date picker (current date)
   * @default moment() - current date
   */
  maxDate = moment();

  /**
   * @type {google.maps.Map}
   * @description Native Google Maps instance reference for map operations and bounds adjustment
   * @default null
   * @see onMapReady() method for initialization
   */
  map: any;

  /**
   * @type {{ startDate: moment.Moment, endDate: moment.Moment }}
   * @description Selected date range from primary date picker component
   * @default null
   * @side-effect Updated in onDateChanged() and synchronized with fromDate/toDate strings
   */
  selected: { startDate: moment.Moment, endDate: moment.Moment };

  /**
   * @type {string | null}
   * @description Start date in YYYY-MM-DD format for API activity data fetching
   * @default null
   * @format YYYY-MM-DD (ISO format)
   * @side-effect Set in onDateChanged() and used in searchData() API call
   */
  public fromDate: string | null = null;

  /**
   * @type {string | null}
   * @description End date in YYYY-MM-DD format for API activity data fetching
   * @default null
   * @format YYYY-MM-DD (ISO format)
   * @side-effect Set in onDateChanged() and used in searchData() API call
   */
  public toDate: string | null = null;

  /**
   * @type {{ startDate: moment.Moment, endDate: moment.Moment }}
   * @description Google Maps date filter selection from maps-specific date picker
   * @default null
   * @side-effect Used in applyFilterWithFilterType() for filtering map markers by date range
   */
  gmapsDateselected: { startDate: moment.Moment, endDate: moment.Moment };

  /**
   * @type {string}
   * @description Login user's person type loaded from localStorage
   * @default null (loaded in ngOnInit)
   * @possible-values EMPLOYEE, Business Admin, Provider Manager, Farmer, etc.
   * @role-specific Used for role-based technician loading logic
   */
  public loginPersonType: string;

  /**
   * @type {string}
   * @description Login user's person ID loaded from localStorage for role-based filtering
   * @default null (loaded in ngOnInit)
   * @side-effect Used to filter technicians reporting to the logged-in manager (Provider Manager role)
   */
  public loginPersonId: string;

  /**
   * @type {string}
   * @description Login user's business name loaded from localStorage
   * @default null (loaded in ngOnInit)
   */
  public loginUserBusinessName: string;

  /**
   * @type {string}
   * @description Login user's business category loaded from localStorage
   * @default null (loaded in ngOnInit)
   * @possible-values Provider, Farmer, Business
   * @side-effect Used to determine if user is Provider type for role-based logic
   */
  public loginUserBusinessCategory: string;

  /**
   * @type {string}
   * @description Login user's business ID loaded from localStorage for business-specific filtering
   * @default null (loaded in ngOnInit)
   * @side-effect Used in getPersonsbyBusinessID() to fetch business-specific technicians
   */
  public loginbusinessID: string;

  /**
   * @type {string}
   * @description Search keyword for autocomplete functionality in technician dropdown
   * @default 'name' - set in ngOnInit
   * @side-effect Used as filter key in autocomplete matching
   */
  public keyword: string;

  /**
   * @type {Array}
   * @description Array of filtered technician objects ready for dropdown selection
   * @structure [{name: 'John Doe', id: '...', ...personsData}, ...]
   * @default []
   * @side-effect Populated in ngOnInit after role-based filtering; updated by filterBusinesses()
   */
  public techniciansData = [];

  /**
   * @type {Array}
   * @description Raw activity data response from getActivityDate() API call
   * @default null
   * @side-effect Contains FeedLog, ObservationLog, TreatmentLog, Sampling, HarvestLog, Readings, OnBoarding arrays
   */
  public reportsData: any;

  /**
   * @type {Array}
   * @description Temporary array used during technician filtering operations
   * @default []
   */
  public tempArray = [];

  /**
   * @type {boolean}
   * @description Flag indicating if multiple technicians are selected (All option)
   * @default false
   * @side-effect Set to true when "All" technician option is selected in dropdown
   */
  public tableHeaderFlag: boolean = false;

  /**
   * @type {boolean}
   * @description Flag tracking if chart visualization is currently open
   * @default false
   * @side-effect Toggled by openChart() and closeMapsGraph() methods
   */
  public isChartClicked: boolean = false;

  /**
   * @type {boolean}
   * @description Flag tracking if Google Maps visualization is currently open
   * @default false
   * @side-effect Toggled by openMaps() and closeMapsGraph() methods
   */
  public isMapsClicked: boolean = false;

  /**
   * @type {Array}
   * @description Array of unique farm/customer IDs extracted from activity location data
   * @default []
   * @side-effect Populated during activity parsing (getFeedLogData, getObservationLog, etc.)
   * @usage Used for deduplication in removeSingleTechnicianTableDuplicates()
   */
  public customerArray = [];

  /**
   * @type {Array}
   * @description Array of {lat, long} coordinate objects from activity location tracking
   * @structure [{lat: number, long: number}, ...]
   * @default []
   * @side-effect Populated during activity parsing; used for Google Maps bounds calculation
   */
  public latitudeLongitudeArray = [];

  /**
   * @type {Array}
   * @description Backup array of location coordinates (currently not actively used)
   * @default []
   */
  public latLangResultArray = [];

  /**
   * @type {Array}
   * @description Array of activity type names found in the data (Feed Log, Observation Log, etc.)
   * @structure ['Feed Log', 'Observation Log', 'Treatment Log', ...]
   * @default []
   * @side-effect Used in removeSingleTechnicianTableDuplicates() to filter and organize records
   */
  public allActivitiesArray = [];

  /**
   * @type {Array}
   * @description Main table data containing all processed and deduplicated activity records
   * @structure [{name, id, date, punchin, punchout, visitTime, customer, activityName, ...}, ...]
   * @default []
   * @side-effect Populated by arrangeSingleTechnicianTableData() and arrangeMultipleTechnicianTableData()
   * @usage Displayed in "All Activities" tab and passed to initializeGoogleMaps()
   */
  public mainTableData = [];

  /**
   * @type {string}
   * @description Name of the currently selected technician for single technician view
   * @default null
   * @side-effect Set in arrangeSingleTechnicianTableData() for display header
   */
  public technicianName: string;

  /**
   * @type {boolean}
   * @description Flag indicating if no data is found for search criteria
   * @default false
   * @side-effect Set to true when getActivityDate() returns empty array
   * @usage Shows "No Data Found" message in template
   */
  public showNoDataFlag: boolean = false;

  /**
   * @type {boolean}
   * @description Flag controlling display of "All Activities" tab
   * @default false
   * @side-effect Set to true when both activities and leads data are available
   * @usage Controls tab visibility in template
   */
  public isAllTabFalg: boolean = false;

  /**
   * @type {boolean}
   * @description Flag controlling display of "Leads" tab
   * @default false
   * @side-effect Set to true when leads data is available
   * @usage Controls tab visibility in template
   */
  public isLeadsTabFlag: boolean = false;

  /**
   * @type {boolean}
   * @description Flag controlling visibility of maps/charts container
   * @default false
   * @side-effect Set by openMaps() and openChart() methods; closed by closeMapsGraph()
   */
  public closeMapsCharts: boolean = false;

  /**
   * @type {boolean}
   * @description Page load indicator flag for progress spinner display
   * @default false
   * @side-effect Set during data loading operations in searchData() and arrangement methods
   * @usage Shows loading spinner while data is being processed
   */
  public pgLoad: boolean = false;

  /**
   * @type {number}
   * @description Total count of unique farm/customer locations in activity data
   * @default 0
   */
  public locationsLength = 0;

  /**
   * @type {number}
   * @description Running total count of all activities across all technicians and dates
   * @default 0
   * @side-effect Incremented in removeSingleTechnicianTableDuplicates() after deduplication
   */
  public totalActivities = 0;

  /**
   * @type {Array}
   * @description Table data for leads specifically with conversion metrics
   * @structure [{type, date, name, customer, mobile, farmAt, pincode, cultureType, species, area, ponds, conversions, ...}, ...]
   * @default []
   * @side-effect Populated by leadsTableData() parser
   * @usage Displayed in "Leads" tab; exported to Excel via exportExcel()
   */
  public leadsTableRecords = [];

  /**
   * @type {number}
   * @description Calculated percentage ratio of Shrimp culture leads vs total
   * @default 0
   * @format Percentage (0-100)
   * @calculation (shrimp_count / (shrimp_count + fish_count)) * 100
   * @side-effect Calculated in arrangeLeadsTableData()
   */
  public shrimpRatio: any = 0;

  /**
   * @type {number}
   * @description Calculated percentage ratio of Fish culture leads vs total
   * @default 0
   * @format Percentage (0-100)
   * @calculation (fish_count / (shrimp_count + fish_count)) * 100
   * @side-effect Calculated in arrangeLeadsTableData()
   */
  public fishRatio: any = 0;

  /**
   * @type {number}
   * @description Total count of leads (not converted + followups + closed)
   * @default 0
   * @calculation tempLeads.length + tempFollowUp.length + tempClose.length
   * @side-effect Calculated in arrangeLeadsTableData()
   */
  public totalLeads: any = 0;

  /**
   * @type {number}
   * @description Total count of leads successfully converted to customers
   * @default 0
   * @side-effect Calculated in arrangeLeadsTableData() by filtering type === 'Conversion'
   */
  public totalConversions: any = 0;

  /**
   * @type {number}
   * @description Total culture area (in hectares or acres) covered by all leads
   * @default 0
   * @calculation Sum of totalCultureArea from all lead records
   * @side-effect Calculated in arrangeLeadsTableData() using reduce()
   */
  public totalAreaofLeads: any = 0;

  /**
   * @type {Array | number}
   * @description Array of unique lead locations by pincode
   * @default 0
   * @side-effect Set in arrangeLeadsTableData() using _.uniqBy(leadsTableRecords, 'pincode')
   * @usage Used to count unique geographic locations where leads exist
   */
  public totalLeadLocations: any = 0;

  /**
   * @type {any}
   * @description Application permission object for role-based UI rendering
   * @default null
   */
  public appPermission: any;

  /**
   * @type {string}
   * @description ID of selected technician from dropdown
   * @default null
   * @side-effect Set in selectOption() method
   * @usage Used in searchData() to build techniciansArray for API call
   */
  public technicianItem: any;

  /**
   * @type {string}
   * @description Search keyword for autocomplete in technician dropdown
   * @default 'name' - set in ngOnInit
   * @side-effect Used as filter key in filterBusinesses()
   */
  public techniciankeyword: string;

  /**
   * @type {number}
   * @description Default center latitude for Google Maps (Hyderabad, India)
   * @default 17.4400848
   * @usage Initial map center before bounds adjustment
   */
  public lat = 17.4400848;

  /**
   * @type {number}
   * @description Default center longitude for Google Maps (Hyderabad, India)
   * @default 78.4202012
   * @usage Initial map center before bounds adjustment
   */
  public lng = 78.4202012;

  /**
   * @type {any}
   * @description Current location tracking info details displayed in map info window
   * @default null
   * @side-effect Set in onLocationMouseOverClick() with location coordinate data
   * @usage Displayed in info window template with timestamp and farm details
   */
  public locationTrackingInfoDetails: any;

  /**
   * @type {{ url: string, scaledSize: { width: number, height: number } }}
   * @description Custom marker icon configuration for Google Maps location pins
   * @default { url: MapsPinIcon.svg, width: 100, height: 100 }
   * @usage Applied to all markers in Google Maps for consistent visual branding
   */
  icon = {
    url: '../../../../../../assets/img/activity_tracker_imgs/MapsPinIcon.svg',
    scaledSize: {
      width: 100,
      height: 100
    }
  };

  /**
   * @type {boolean}
   * @description Flag indicating if any info window is currently open on maps
   * @default false
   * @side-effect Set in onLocationMouseOverClick() to track open window state
   */
  public infoWindowOpened: boolean = false;

  /**
   * @type {number}
   * @description Current Google Maps zoom level
   * @default 13
   * @range 0-20 (limited to max 20 in onMapReady())
   * @side-effect Adjusted by onMapReady() and limited to prevent over-zooming
   */
  public zoom: number = 13;

  /**
   * @type {Array}
   * @description Array of processed location coordinates for Google Maps marker placement
   * @structure [{latitude: string, longitude: string, place, dateTime, farm, ...}, ...]
   * @default []
   * @side-effect Populated in initializeGoogleMaps() from filtered main table data
   * @usage Used to render markers and calculate map bounds
   */
  public googleMapsLatlongsPositions: any[] = [];

  /**
   * @type {boolean}
   * @description Flag tracking if technician dropdown is open
   * @default false
   * @side-effect Toggled by filterBusinesses() and selectOption() methods
   */
  public isDropdownOpen = false;

  /**
   * @type {Array}
   * @description Filtered list of technicians matching current search input
   * @default []
   * @side-effect Updated in filterBusinesses() based on user input
   * @usage Displayed in dropdown options
   */
  public filteredOptions: any[] = [];

  /**
   * @type {string}
   * @description Placeholder text for business/technician search input
   * @default 'Enter the business name'
   * @side-effect Set to undefined for certain roles (EMPLOYEE, Business Admin, Provider Manager)
   */
  public businessSearch: string = 'Enter the business name';

  /**
   * @type {boolean}
   * @description Flag indicating if form has been submitted and validation checked
   * @default false
   * @side-effect Set to true in searchData() for validation error display
   * @usage Shows validation error messages in template when submitted && invalid
   */
  public submitted: boolean;

  /**
   * @type {boolean}
   * @description Flag indicating if a technician has been selected for maps filtering
   * @default false
   * @side-effect Set in onFiltersApplied() to control filter application logic
   */
  public isPersonSelected: boolean;

  /**
   * @type {boolean}
   * @description Flag indicating if a date range has been selected for maps filtering
   * @default false
   * @side-effect Set in onFiltersApplied() and applyFilterWithFilterType()
   */
  public isDateRangeSelected: boolean;

  /**
   * @type {string}
   * @description Name of selected technician for maps-level filtering
   * @default null
   * @side-effect Set in onFiltersApplied() for person-specific location filtering
   * @usage Used in applyFilterWithFilterType() to filter positions by personName
   */
  public selectedPersonName: string;

  /**
   * @type {Array}
   * @description Array of technician person names for maps display
   * @structure ['John Doe', 'Jane Smith', ...]
   * @default null
   * @side-effect Populated in initializeGoogleMaps() by mapping googleMapsLatlongsPositions
   */
  public technicianPersons: any[];

  /**
   * @type {Array}
   * @description Backup copy of googleMapsLatlongsPositions for filter reset functionality
   * @default null
   * @side-effect Created and used in onFiltersApplied() for reset-to-original functionality
   */
  public googleMapsLatlongsPositionsCopy: any;

  /**
   * @type {FormGroup}
   * @description Secondary date range form with independent start and end date fields
   * @default Created in constructor with start/end defaults to today's date
   * @validation Both fields default to new Date() without explicit validators
   */
  dateRangeForm: FormGroup;

  /**
   * @type {boolean}
   * @description Flag indicating if date range filter has been applied
   * @default false
   * @side-effect Set in onDateRangeChange() and clearDateRange()
   * @usage Controls visual state of applied filter indication
   */
  dateFilterApplied = false;
  // maxDate = new Date();


  // ============================================================================
  // CONSTRUCTOR & SERVICE DEPENDENCY INJECTION
  // ============================================================================

  /**
   * @method constructor
   * @description Initializes component with all required services and reactive forms
   * 
   * Initializes two FormGroups:
   * 1. trackerSearchForm: Main search form with selectTechnician (required) and nested dateRangeField (start, end - required)
   * 2. dateRangeForm: Alternative date range form with defaults set to today's date
   * 
   * Sets alwaysShowCalendars to true for date picker UI visibility
   * Calls onDateRangeChange() to initialize date filter state
   * 
   * @service ToastrService - Toast notifications for user feedback messages
   * @service FormBuilder - Reactive form creation and group building
   * @service PersonsService - Technician/person data loading from API
   * @service FeedLogService - Activity data fetching (Feed Log, Sampling, etc.)
   * @service ActivityTrackerService - Activity tracking specific operations
   * @service ExcelService - Excel export functionality for reports
   * @service DatePipe - Date formatting utility
   * @service CommonMethodsService - Shared utilities and error handling
   * @service LocationStrategy - Navigation history tracking
   * @service PreviousRouteService - Route history management for navigation
   * @service Renderer2 - DOM manipulation for fullscreen and styling operations
   */
  constructor(private toastrService: ToastrService, public formBuilder: FormBuilder, public personsService: PersonsService, public feedLogService: FeedLogService, public activityTrackerService: ActivityTrackerService,
              private excelService: ExcelService, public datepipe: DatePipe, public commonMethodsService: CommonMethodsService, private locationStrategy: LocationStrategy, private previousRouteService: PreviousRouteService,
              private renderer: Renderer2) {
    this.alwaysShowCalendars = true;
    this.trackerSearchForm = this.formBuilder.group({
      selectTechnician: ['', Validators.required],
      dateRangeField: this.formBuilder.group({
        start: [null, Validators.required],
        end: [null, Validators.required]
      })
    });
    this.dateRangeForm = this.formBuilder.group({
      start: [new Date()], // default to today
      end: [new Date()]
    });
    this.onDateRangeChange();
  }

  // ============================================================================
  // LIFECYCLE HOOKS
  // ============================================================================

  /**
   * @lifecycle ngOnInit
   * @async
   * @description Component initialization with role-based technician data loading and form setup
   * 
   * Initialization Flow:
   * 1. Set autocomplete keywords to 'name' for both technician dropdown and map filtering
   * 2. Initialize date range selected flag based on gmapsDateselected existence
   * 3. Load login user details from localStorage:
   *    - loginPersonType (EMPLOYEE, Business Admin, Provider Manager, etc.)
   *    - loginPersonId, loginUserBusinessName, loginUserBusinessCategory
   *    - loginbusinessID
   * 4. Set previous route for navigation history tracking
   * 5. Load technicians based on role-specific business assignment:
   *    - **EMPLOYEE**: getAllPersons() then filter for personRoleID === '61712face37ea5001eb52ab8' (Provider Technician)
   *    - **Business Admin (Provider)**: getPersonsbyBusinessID(loginbusinessID)
   *    - **Provider Manager (Provider)**: getPersonsbyBusinessID() filtered by reportingTo.id === loginPersonId
   * 6. Filter promiseResults to extract only those with personType[0].name === 'Provider Technician'
   * 7. Transform filtered technicians into dropdown format with computed name field
   * 8. Initialize filteredOptions and tempArray with technicians data
   * 
   * @throws Catches all errors via commonMethodsService.userFriendlyAlertMessages()
   * @side-effect
   * - Populates techniciansData array with role-filtered technicians
   * - Sets filteredOptions for dropdown rendering
   * - Initializes all role-related login variables from localStorage
   * - Sets previous route in PreviousRouteService
   * 
   * @example
   * // EMPLOYEE role initialization
   * await this.personsService.getAllPersons()
   * → Filter for personRoleID === '61712face37ea5001eb52ab8'
   * → Transform to {name: 'John Doe', ...personData}
   * → Populate techniciansData and filteredOptions
   */
  public async ngOnInit() {
    try {
      this.keyword = 'name'; // detects to test in autocomplete
      this.techniciankeyword = 'name';
      this.isDateRangeSelected = this.gmapsDateselected == undefined ? false : true;
      this.loginPersonType = localStorage.getItem('loginPersonType');
      this.loginPersonId = localStorage.getItem('loginPersonId');
      this.loginUserBusinessName = localStorage.getItem('loginBusinessName');
      this.loginUserBusinessCategory = localStorage.getItem('loginBusinessCategory');
      this.loginbusinessID = localStorage.businessID;
      this.previousRouteService.setPreviousRoute(this.locationStrategy.path());
      // Fetching Provider Technician Details based on login user Admin or Business User
      if (this.loginPersonType == 'EMPLOYEE') {
        this.businessSearch = undefined;
        this.trackerSearchForm.get('selectTechnician').patchValue('');
        let persons = await this.personsService.getAllPersons();
        this.promiseResults = persons.filter(person => person && person.personType[0] && person.personType[0].personRoleID === '61712face37ea5001eb52ab8');
      } else if (this.loginPersonType == 'Business Admin' && this.loginUserBusinessCategory == 'Provider') {
        this.businessSearch = undefined;
        this.trackerSearchForm.get('selectTechnician').patchValue('');
        this.promiseResults = await this.personsService.getPersonsbyBusinessID(this.loginbusinessID);
      } else if (this.loginPersonType == 'Provider Manager' && this.loginUserBusinessCategory == 'Provider') {
        this.businessSearch = undefined;
        this.trackerSearchForm.get('selectTechnician').patchValue(this.loginUserBusinessName);
        let promiseResults = await this.personsService.getPersonsbyBusinessID(this.loginbusinessID);
        this.promiseResults = promiseResults.filter(technician => technician && technician.reportingTo && technician.reportingTo.id == this.loginPersonId);
      }
      // Assigning Data To Dropdown Object
      this.promiseResults.forEach((item) => {
        if (item.personType) {
          if (item.personType[0].name == 'Provider Technician') {
            let dataObj = {
              'name': item.firstName + ' ' + item.lastName,
              ...item
            };
            this.techniciansData.push(dataObj);
          }
        }
      });
      this.filteredOptions = this.techniciansData;
      this.tempArray = this.filteredOptions;
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method onDateRangeChange
   * @description Listener for dateRangeForm value changes to update dateFilterApplied flag
   * 
   * When both start and end dates are present, sets dateFilterApplied to true indicating
   * active filter state for UI indication
   * 
   * @side-effect Sets dateFilterApplied boolean flag when both dates are present
   * @example
   * // Triggered automatically when date range form values change
   * onDateRangeChange() // dateFilterApplied = true if start && end exist
   */
  onDateRangeChange() {
    const { start, end } = this.dateRangeForm.value;
    if (start && end) {
      this.dateFilterApplied = true;
      // Apply your filter logic here
    }
  }

  /**
   * @method onFilterdPickerClosed
   * @description Handler for date picker closure event to reapply filters
   * 
   * When picker closes and both dates are selected, sets dateFilterApplied flag and
   * triggers onDateRangeChange() to update filter state
   * 
   * @side-effect Sets dateFilterApplied flag and reapplies date change handler
   */
  onFilterdPickerClosed() {
    const { start, end } = this.dateRangeForm.value;
    if (start && end) {
      this.dateFilterApplied = true;
      this.onDateRangeChange();
    }
  }

  /**
   * @method clearDateRange
   * @description Clears the secondary date range form and resets date filter flag
   * 
   * Resets dateRangeForm to pristine state and sets dateFilterApplied to false to
   * remove date range filtering from map view
   * 
   * @side-effect Clears dateRangeForm values and resets dateFilterApplied flag
   */
  clearDateRange() {
    this.dateRangeForm.reset();
    this.dateFilterApplied = false;
    // Trigger reset filter
  }

  /**
   * @method f
   * @type {getter}
   * @description Shorthand getter for trackerSearchForm controls for template access
   * 
   * @returns {AbstractControl} trackerSearchForm.controls for use in template validation
   * @example
   * // In template: *ngIf="f.selectTechnician.hasError('required')"
   */
  public get f() {
    return this.trackerSearchForm.controls;
  }

  /**
   * @method dateRangeField
   * @type {getter}
   * @description Getter for dateRangeField form group from trackerSearchForm
   * 
   * Extracts nested dateRangeField FormGroup from main trackerSearchForm for direct access
   * and value retrieval
   * 
   * @returns {FormGroup} dateRangeField nested form group with start and end date controls
   * @example
   * // Get date range values
   * const { start, end } = this.dateRangeField.value;
   */
  get dateRangeField(): FormGroup {
    return this.trackerSearchForm.get('dateRangeField') as FormGroup;
  }

  /**
   * @method filterBusinesses
   * @type {public}
   * @description Real-time autocomplete filter for technician dropdown based on user input
   * 
   * Extracts search value from selectTechnician form control and filters techniciansData
   * array by comparing input against firstName field (case-insensitive partial match)
   * 
   * @side-effect Updates filteredOptions array with matching technicians for dropdown rendering
   * @example
   * // User types "john" in dropdown
   * filterBusinesses() → filters techniciansData for firstName containing "john"
   * → Updates filteredOptions for display
   */
  public filterBusinesses(): void {
    const control = this.trackerSearchForm.get('selectTechnician');
    if (!control) return;
    const value = (control.value || '') as PersonsModel | string;
    const searchText = typeof value === 'string' ? value.toLowerCase() : value.firstName.toLowerCase();
    this.filteredOptions = this.techniciansData.filter(o =>
      o.firstName.toLowerCase().includes(searchText)
    );
  }

  /**
   * @method selectOption
   * @type {public}
   * @param {PersonsModel} business - Selected technician object with firstName, lastName, id
   * @description Handler for technician selection from autocomplete dropdown
   * 
   * Updates form control with selected technician's full name, closes dropdown,
   * sets businessSearch display text, and stores selected technician ID
   * 
   * @side-effect
   * - Updates selectTechnician form value with full name
   * - Sets isDropdownOpen to false
   * - Sets businessSearch display text
   * - Stores technicianItem (selected technician ID) for API calls
   * 
   * @example
   * // User clicks "John Doe" in dropdown
   * selectOption(johnDoeObject)
   * → Updates form value to "John Doe"
   * → Sets technicianItem to johnDoeObject.id
   * → Ready for searchData() call
   */
  public selectOption(business: PersonsModel): void {
    this.trackerSearchForm.get('selectTechnician')!.setValue(business.firstName.concat(' ' + business.lastName));
    this.isDropdownOpen = false;
    this.businessSearch = business.firstName + ' ' + business.lastName;
    this.technicianItem = business.id;
  }

  /**
   * @method searchData
   * @async
   * @description Main search orchestrator that fetches and processes activity data based on selected
   * technician and date range. Executes comprehensive data pipeline from API fetch through deduplication
   * and visualization preparation.
   * 
   * Process Flow:
   * 1. Mark form as submitted and validate trackerSearchForm (technician + date range required)
   * 2. Reset all data arrays and flags to clean state:
   *    - googleMapsLatlongsPositions, mainTableData, latitudeLongitudeArray, customerArray
   *    - totalActivities, leadsTableRecords, shrimpRatio, fishRatio, totalLeads, etc.
   *    - showNoDataFlag, isLeadsTabFlag, isAllTabFalg flags
   * 3. Validate form is complete; if invalid, return without processing
   * 4. Set pgLoad=true to show loading indicator
   * 5. Build techniciansArray:
   *    - If "All" selected → push all technician IDs (excluding "All" option)
   *    - If single technician → push only that technicianItem
   * 6. Call feedLogService.getActivityDate(fromDate, toDate, techniciansArray)
   * 7. Parse returned activities using activity-type-specific parsers:
   *    - getFeedLogData() - Feed log records
   *    - getObservationLog() - Observation records
   *    - getTreatmentLog() - Treatment records
   *    - getSampling() - Sampling records
   *    - getHarvestLog() - Harvest records
   *    - getReadings() - Sensor readings
   *    - getOnBoarding() - Onboarding activities
   * 8. Track lead conversions via leadsTableData() if Leads array exists
   * 9. Deduplicate activities:
   *    - Single technician → arrangeSingleTechnicianTableData() with full dedup
   *    - Multiple technicians → arrangeMultipleTechnicianTableData() with merged results
   * 10. Sort mainTableData by date descending
   * 11. Arrange leads data and calculate analytics (ratios, conversions, area)
   * 12. Determine tab visibility and initialize Google Maps
   * 13. Set pgLoad=false to hide loading indicator
   * 
   * Tab Display Logic:
   * - Both mainTableData and leadsTableRecords → Show All Activities tab + Leads tab
   * - Only leadsTableRecords → Show Leads tab only
   * - Neither → Show "No Data" message
   * 
   * @throws Catches errors via commonMethodsService.userFriendlyAlertMessages()
   * @side-effect
   * - Populates mainTableData, leadsTableRecords, allActivitiesArray
   * - Sets tab visibility flags (isAllTabFalg, isLeadsTabFlag)
   * - Initializes Google Maps with location data
   * - Calculates aggregate metrics (totalActivities, totalLeads, ratios)
   * - Resets loading state (pgLoad)
   * 
   * @example
   * // User flow
   * Select "John Doe" + "2025-01-01 to 2025-01-31" → searchData()
   * → Fetches activities for John from backend
   * → Parses 7 activity types with location data
   * → Deduplicates same-time-same-place activities
   * → Shows main table with All Activities tab
   * → Initializes Google Maps with visit locations
   */
  public async searchData() {
    this.submitted = true;
    // Mark all fields as touched to show errors
    this.trackerSearchForm.markAllAsTouched();
    if (this.trackerSearchForm.invalid) {
      return;
    }
    try {

    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
    this.googleMapsLatlongsPositions = [];
    this.infoWindowOpened = false;
    let techniciansArray: any = [];
    this.mainTableData = [];
    let tempTableArray = [];
    this.latitudeLongitudeArray = [];
    this.customerArray = [];
    this.latLangResultArray = [];
    this.totalActivities = 0;
    this.leadsTableRecords = [];
    this.shrimpRatio = 0;
    this.fishRatio = 0;
    this.totalLeads = 0;
    this.totalConversions = 0;
    this.totalAreaofLeads = 0;
    this.totalLeadLocations = 0;
    this.showNoDataFlag = false;
    this.isLeadsTabFlag = false;
    this.isAllTabFalg = false;
    // validating form , will proceed only if date and technician is entered .
    // for dev use remove this and use before push activate this
    // if not valid then raise a dialog to enter both -- needs to be implemented .
    if (this.trackerSearchForm.valid) {
      this.pgLoad = true;
      let selectedTechnician = this.trackerSearchForm.controls.selectTechnician.value;
      // select technician ids based on dropdown selection ;
      if (selectedTechnician.name == 'All') {
        // if all then push all the technicans id into the array
        this.tableHeaderFlag = true;
        this.filteredOptions.forEach((item) => {
          if (item.name != 'All') {
            techniciansArray.push(item.id);
          }
        });
      } else {
        // if only one technician is selected then push that technican id into the array
        techniciansArray.push(this.technicianItem);
      }
      await this.feedLogService.getActivityDate(this.fromDate, this.toDate, techniciansArray).then((resolve) => {
        this.reportsData = resolve;
      }).catch((reject) => {
        this.toastrService.error(reject);
      });
      if (this.reportsData.length > 0) {
        this.isAllTabFalg = true;
        this.isLeadsTabFlag = false;
        this.reportsData.forEach(element => {
          let record: any = {};
          record.name = element.firstName + ' ' + element.lastName;
          record.id = element.id;
          record.visitRemarks = 'Test'; // need to update after business update
          if (element.Leads.length > 0) {
            this.leadsTableData(record.name, element.Leads);
          }
          // Feed Log Activity
          if (element.FeedLog.length > 0) {
            record.FeedLog = this.getFeedLogData(element);
          } else {
            record.FeedLog = [];
          }
          // Observation Log Activity
          if (element.ObservationLog.length > 0) {
            record.ObservationLog = this.getObservationLog(element);
          } else {
            record.ObservationLog = [];
          }
          // Treatment Log Activity
          if (element.TreatmentLog.length > 0) {
            record.TreatmentLog = this.getTreatmentLog(element);
          } else {
            record.TreatmentLog = [];
          }
          // Sampling Activity
          if (element.Sampling.length > 0) {
            record.Sampling = this.getSampling(element);
          } else {
            record.Sampling = [];
          }
          // Harvest Log Activity
          if (element.HarvestLog.length > 0) {
            record.HarvestLog = this.getHarvestLog(element);
          } else {
            record.HarvestLog = [];
          }
          // Sampling Activity
          if (element.Readings.length > 0) {
            record.Readings = this.getReadings(element);
          } else {
            record.Readings = [];
          }
          // OnBoarding Activity
          if (element.OnBoarding.length > 0) {
            record.OnBoarding = this.getOnBoarding(element);
          } else {
            record.OnBoarding = [];
          }
          tempTableArray.push(record);
        });
        // Finding Unique Arrays of customers & Places
        if (this.customerArray.length > 0) {
          this.customerArray = this.customerArray.filter((el, i, a) => i === a.indexOf(el));
        }
        // arrange each table data based on the criteria
        if (techniciansArray.length == 1 && tempTableArray.length == 1) {
          // One Technician is selected
          this.mainTableData = this.arrangeSingleTechnicianTableData(tempTableArray);
          this.pgLoad = true;
          this.mainTableData = this.mainTableData.sort((a, b) => (new Date(b.date)).valueOf() - (new Date(a.date)).valueOf());
          if (this.mainTableData.length >= 0) {
            this.pgLoad = false;
          }
        } else {
          // all technicians are selected .
          this.arrangeMultipleTechnicianTableData(tempTableArray);
        }
        if (this.leadsTableRecords.length > 0) {
          this.arrangeLeadsTableData();
        }
        if (this.leadsTableRecords.length > 0 && this.mainTableData.length > 0) {
          this.allTab();
        } else if (this.leadsTableRecords.length > 0 && this.mainTableData.length == 0) {
          this.leadsTab();
        }
      } else {
        this.showNoDataFlag = true;
        this.pgLoad = false;
      }
    } else {
      this.submitted = true;
      if (this.trackerSearchForm.invalid) {
        return;
      }
      this.pgLoad = false;
    }
  }

  /**
   * @method getFeedLogData
   * @param {any} element - Technician record containing FeedLog array from API response
   * @type {public}
   * @returns {Array} Array of parsed feed log activity records with location and timing data
   * @description Parses Feed Log activities from raw API response into displayable records
   * 
   * For each feed log entry, extracts:
   * - Activity metadata: id, date (formatted dd-MMM-yyyy), activityName = "Feed Log"
   * - Location tracking: atFarmOnlatitude, atFarmOnlongitude from LocationTrackerDetails[0]
   * - Time tracking: puncInTime, punchOutTime from LocationTrackerDetails[0]
   * - Visit duration: Calculated as minutes between punchOutTime and puncInTime
   * - Place names: place (on location), place_off (off location)
   * - Farm/Customer: Extracted from FarmSiteDetails[0].businessID.businessName
   * 
   * Side Effects During Parsing:
   * - Adds valid locations to latitudeLongitudeArray for map bounds
   * - Appends customer IDs to customerArray for deduplication
   * 
   * @side-effect
   * - Populates latitudeLongitudeArray with {lat, long} coordinates
   * - Appends unique customer IDs to customerArray
   * - Tracks location points for map visualization
   * 
   * @example
   * // Raw Feed Log entry
   * {id: 'fl123', last_feed_given_date: '2025-01-15', LocationTrackerDetails: [{...}], ...}
   * 
   * // Parsed output
   * {id: 'fl123', date: '15-Jan-2025', activityName: 'Feed Log', 
   *  latitude: 17.44, longitude: 78.42, visitTime: 30, customer: 'Farm ABC', ...}
   */
  public getFeedLogData(element: any) {
    let feedLogtArray = [];
    element.FeedLog.forEach((temp) => {
      let feedLogElement: any = {};
      feedLogElement.id = temp.id;
      feedLogElement.date = this.datepipe.transform(temp.last_feed_given_date, 'dd-MMM-yyyy');
      feedLogElement.activityName = 'Feed Log';
      feedLogElement.place = '';
      feedLogElement.place_off = '';
      if (temp.LocationTrackerDetails.length > 0) {
        feedLogElement.atFarmOnlatitude = temp.LocationTrackerDetails[0].atFarmOnlatitude;
        feedLogElement.atFarmOnlongitude = temp.LocationTrackerDetails[0].atFarmOnlongitude;
        feedLogElement.puncInTime = temp.LocationTrackerDetails[0].atFarmOnDateTime;
        feedLogElement.punchOutTime = temp.LocationTrackerDetails[0].atFarmOffDateTime;
        feedLogElement.place = temp.LocationTrackerDetails[0].place;
        feedLogElement.place_off = temp.LocationTrackerDetails[0].place_off;
        if (feedLogElement.punchOutTime) {
          feedLogElement.visitTime = Math.trunc(moment.duration(moment(new Date(feedLogElement.punchOutTime)).diff(moment(new Date(feedLogElement.puncInTime)))).asMinutes());
        }
      } else {
        feedLogElement.atFarmOnlatitude = '';
        feedLogElement.atFarmOnlongitude = '';
        feedLogElement.puncInTime = '';
        feedLogElement.punchOutTime = '';
        feedLogElement.visitTime = '';
        feedLogElement.place = '';
        feedLogElement.place_off = '';
      }
      if (temp.FarmSiteDetails.length > 0) {
        feedLogElement.customer = temp.FarmSiteDetails[0].businessID.businessName;
        feedLogElement.customerId = temp.FarmSiteDetails[0].id;
      } else {
        feedLogElement.customer = '';
        feedLogElement.customerId = '';
      }
      if (feedLogElement.customerId) {
        this.customerArray.push(feedLogElement.customerId);
      }
      if (feedLogElement.atFarmOnlatitude && feedLogElement.atFarmOnlongitude) {
        let item: any = {
          lat: feedLogElement.atFarmOnlatitude,
          long: feedLogElement.atFarmOnlongitude
        };
        this.latitudeLongitudeArray.push(item);
      }
      feedLogtArray.push(feedLogElement);
    });
    return feedLogtArray;
  }

  /**
   * @method getObservationLog
   * @param {any} element - Technician record containing ObservationLog array from API response
   * @type {public}
   * @returns {Array} Array of parsed observation log activity records
   * @description Parses Observation Log activities - identical parsing logic to getFeedLogData
   * with activityName = "Observation Log" and date field from temp.date instead of last_feed_given_date
   * 
   * @side-effect Populates latitudeLongitudeArray and customerArray during parsing
   */
  public getObservationLog(element: any) {
    let observationArray = [];
    element.ObservationLog.forEach((temp) => {
      let observationLogElement: any = {};
      observationLogElement.id = temp.id;
      observationLogElement.date = this.datepipe.transform(temp.date, 'dd-MMM-yyyy');
      observationLogElement.activityName = 'Observation Log';
      observationLogElement.place = '';
      observationLogElement.place_off = '';
      if (temp.LocationTrackerDetails.length > 0) {
        observationLogElement.atFarmOnlatitude = temp.LocationTrackerDetails[0].atFarmOnlatitude;
        observationLogElement.atFarmOnlongitude = temp.LocationTrackerDetails[0].atFarmOnlongitude;
        observationLogElement.puncInTime = temp.LocationTrackerDetails[0].atFarmOnDateTime;
        observationLogElement.punchOutTime = temp.LocationTrackerDetails[0].atFarmOffDateTime;
        observationLogElement.place = temp.LocationTrackerDetails[0].place;
        observationLogElement.place_off = temp.LocationTrackerDetails[0].place_off;
        if (observationLogElement.punchOutTime) {
          observationLogElement.visitTime = Math.trunc(moment.duration(moment(new Date(observationLogElement.punchOutTime)).diff(moment(new Date(observationLogElement.puncInTime)))).asMinutes());
        }
      } else {
        observationLogElement.atFarmOnlatitude = '';
        observationLogElement.atFarmOnlongitude = '';
        observationLogElement.puncInTime = '';
        observationLogElement.punchOutTime = '';
        observationLogElement.visitTime = '';
        observationLogElement.place = '';
        observationLogElement.place_off = '';
      }
      if (temp.FarmSiteDetails.length > 0) {
        observationLogElement.customer = temp.FarmSiteDetails[0].businessID.businessName;
        observationLogElement.customerId = temp.FarmSiteDetails[0].id;
      } else {
        observationLogElement.customer = '';
        observationLogElement.customerId = '';
      }
      if (observationLogElement.customerId) {
        this.customerArray.push(observationLogElement.customerId);
      }
      if (observationLogElement.atFarmOnlatitude && observationLogElement.atFarmOnlongitude) {
        let item: any = {
          lat: observationLogElement.atFarmOnlatitude,
          long: observationLogElement.atFarmOnlongitude
        };
        this.latitudeLongitudeArray.push(item);
      }
      observationArray.push(observationLogElement);
    });
    return observationArray;
  }

  /**
   * @method getTreatmentLog
   * @param {any} element - Technician record containing TreatmentLog array from API response
   * @type {public}
   * @returns {Array} Array of parsed treatment log activity records
   * @description Parses Treatment Log activities - identical parsing structure to getFeedLogData
   * with activityName = "Treatment Log"
   * 
   * @side-effect Populates latitudeLongitudeArray and customerArray during parsing
   */
  public getTreatmentLog(element: any) {
    let treatmentArray = [];
    element.TreatmentLog.forEach((temp) => {
      let treatmentElement: any = {};
      treatmentElement.id = temp.id;
      treatmentElement.date = this.datepipe.transform(temp.date, 'dd-MMM-yyyy');
      treatmentElement.activityName = 'Treatment Log';
      treatmentElement.place = '';
      treatmentElement.place_off = '';
      if (temp.LocationTrackerDetails.length > 0) {
        treatmentElement.atFarmOnlatitude = temp.LocationTrackerDetails[0].atFarmOnlatitude;
        treatmentElement.atFarmOnlongitude = temp.LocationTrackerDetails[0].atFarmOnlongitude;
        treatmentElement.puncInTime = temp.LocationTrackerDetails[0].atFarmOnDateTime;
        treatmentElement.punchOutTime = temp.LocationTrackerDetails[0].atFarmOffDateTime;
        treatmentElement.place = temp.LocationTrackerDetails[0].place;
        treatmentElement.place_off = temp.LocationTrackerDetails[0].place_off;
        if (treatmentElement.punchOutTime) {
          treatmentElement.visitTime = Math.trunc(moment.duration(moment(new Date(treatmentElement.punchOutTime)).diff(moment(new Date(treatmentElement.puncInTime)))).asMinutes());
        }
      } else {
        treatmentElement.atFarmOnlatitude = '';
        treatmentElement.atFarmOnlongitude = '';
        treatmentElement.puncInTime = '';
        treatmentElement.punchOutTime = '';
        treatmentElement.visitTime = '';
        treatmentElement.place = '';
        treatmentElement.place_off = '';
      }
      if (temp.FarmSiteDetails.length > 0) {
        treatmentElement.customer = temp.FarmSiteDetails[0].businessID.businessName;
        treatmentElement.customerId = temp.FarmSiteDetails[0].id;
      } else {
        treatmentElement.customer = '';
        treatmentElement.customerId = '';
      }
      if (treatmentElement.customerId) {
        this.customerArray.push(treatmentElement.customerId);
      }
      if (treatmentElement.atFarmOnlatitude && treatmentElement.atFarmOnlongitude) {
        let item: any = {
          lat: treatmentElement.atFarmOnlatitude,
          long: treatmentElement.atFarmOnlongitude
        };
        this.latitudeLongitudeArray.push(item);
      }
      treatmentArray.push(treatmentElement);
    });
    return treatmentArray;
  }

  /**
   * @method getSampling
   * @param {any} element - Technician record containing Sampling array from API response
   * @type {public}
   * @returns {Array} Array of parsed sampling activity records
   * @description Parses Sampling activities with identical location/timing extraction to feed logs
   * Uses activityName = "Sampling"
   * 
   * @side-effect Populates latitudeLongitudeArray and customerArray
   */
  public getSampling(element: any) {
    let samplingArray = [];
    element.Sampling.forEach((temp) => {
      let samplingElement: any = {};
      samplingElement.id = temp.id;
      samplingElement.date = this.datepipe.transform(temp.date, 'dd-MMM-yyyy');
      samplingElement.activityName = 'Sampling';
      samplingElement.place = '';
      samplingElement.place_off = '';
      if (temp.LocationTrackerDetails.length > 0) {
        samplingElement.atFarmOnlatitude = temp.LocationTrackerDetails[0].atFarmOnlatitude;
        samplingElement.atFarmOnlongitude = temp.LocationTrackerDetails[0].atFarmOnlongitude;
        samplingElement.puncInTime = temp.LocationTrackerDetails[0].atFarmOnDateTime;
        samplingElement.punchOutTime = temp.LocationTrackerDetails[0].atFarmOffDateTime;
        samplingElement.place = temp.LocationTrackerDetails[0].place;
        samplingElement.place_off = temp.LocationTrackerDetails[0].place_off;
        if (samplingElement.punchOutTime) {
          samplingElement.visitTime = Math.trunc(moment.duration(moment(new Date(samplingElement.punchOutTime)).diff(moment(new Date(samplingElement.puncInTime)))).asMinutes());
        }
      } else {
        samplingElement.atFarmOnlatitude = '';
        samplingElement.atFarmOnlongitude = '';
        samplingElement.puncInTime = '';
        samplingElement.punchOutTime = '';
        samplingElement.visitTime = '';
        samplingElement.place = '';
        samplingElement.place_off = '';
      }
      if (temp.FarmSiteDetails.length > 0) {
        samplingElement.customer = temp.FarmSiteDetails[0].businessID.businessName;
        samplingElement.customerId = temp.FarmSiteDetails[0].id;
      } else {
        samplingElement.customer = '';
        samplingElement.customerId = '';
      }
      if (samplingElement.customerId) {
        this.customerArray.push(samplingElement.customerId);
      }
      if (samplingElement.atFarmOnlatitude && samplingElement.atFarmOnlongitude) {
        let item: any = {
          lat: samplingElement.atFarmOnlatitude,
          long: samplingElement.atFarmOnlongitude
        };
        this.latitudeLongitudeArray.push(item);
      }
      samplingArray.push(samplingElement);
    });
    return samplingArray;
  }

  /**
   * @method getHarvestLog
   * @param {any} element - Technician record containing HarvestLog array from API response
   * @type {public}
   * @returns {Array} Array of parsed harvest log activity records
   * @description Parses Harvest Log activities with identical parsing to other activity types
   * Uses activityName = "Harvest Logs"
   * 
   * @side-effect Populates latitudeLongitudeArray and customerArray
   */
  public getHarvestLog(element: any) {
    let harvestLogArray = [];
    element.HarvestLog.forEach((temp) => {
      let harvestlogElement: any = {};
      harvestlogElement.id = temp.id;
      harvestlogElement.date = this.datepipe.transform(temp.date, 'dd-MMM-yyyy');
      harvestlogElement.activityName = 'Harvest Logs';
      harvestlogElement.place = '';
      harvestlogElement.place_off = '';
      if (temp.LocationTrackerDetails.length > 0) {
        harvestlogElement.atFarmOnlatitude = temp.LocationTrackerDetails[0].atFarmOnlatitude;
        harvestlogElement.atFarmOnlongitude = temp.LocationTrackerDetails[0].atFarmOnlongitude;
        harvestlogElement.puncInTime = temp.LocationTrackerDetails[0].atFarmOnDateTime;
        harvestlogElement.punchOutTime = temp.LocationTrackerDetails[0].atFarmOffDateTime;
        harvestlogElement.place = temp.LocationTrackerDetails[0].place;
        harvestlogElement.place_off = temp.LocationTrackerDetails[0].place_off;
        if (harvestlogElement.punchOutTime) {
          harvestlogElement.visitTime = Math.trunc(moment.duration(moment(new Date(harvestlogElement.punchOutTime)).diff(moment(new Date(harvestlogElement.puncInTime)))).asMinutes());
        }
      } else {
        harvestlogElement.atFarmOnlatitude = '';
        harvestlogElement.atFarmOnlongitude = '';
        harvestlogElement.puncInTime = '';
        harvestlogElement.punchOutTime = '';
        harvestlogElement.visitTime = '';
        harvestlogElement.place = '';
        harvestlogElement.place_off = '';
      }
      if (temp.FarmSiteDetails.length > 0) {
        harvestlogElement.customer = temp.FarmSiteDetails[0].businessID.businessName;
        harvestlogElement.customerId = temp.FarmSiteDetails[0].id;
      } else {
        harvestlogElement.customer = '';
        harvestlogElement.customerId = '';
      }
      if (harvestlogElement.customerId) {
        this.customerArray.push(harvestlogElement.customerId);
      }
      if (harvestlogElement.atFarmOnlatitude && harvestlogElement.atFarmOnlongitude) {
        let item: any = {
          lat: harvestlogElement.atFarmOnlatitude,
          long: harvestlogElement.atFarmOnlongitude
        };
        this.latitudeLongitudeArray.push(item);
      }
      harvestLogArray.push(harvestlogElement);
    });
    return harvestLogArray;
  }

  /**
   * @method getReadings
   * @param {any} element - Technician record containing Readings array from API response
   * @type {public}
   * @returns {Array} Array of parsed sensor reading activity records
   * @description Parses Readings (sensor data) activities with timezone adjustment
   * Uses activityName = "Readings". Date is adjusted by 330 minutes (IST timezone offset)
   * 
   * @side-effect Populates latitudeLongitudeArray and customerArray
   * @note Special date handling: new Date(new Date(temp.time).getTime() - 330 * 60 * 1000)
   */
  public getReadings(element: any) {
    let readingsArray = [];
    element.Readings.forEach((temp) => {
      let readingsElement: any = {};
      readingsElement.id = temp.id;
      readingsElement.date = this.datepipe.transform(new Date(new Date(temp.time).getTime() - 330 * 60 * 1000), 'dd-MMM-yyyy');
      readingsElement.activityName = 'Readings';
      readingsElement.place = '';
      readingsElement.place_off = '';
      if (temp.LocationTrackerDetails.length > 0) {
        readingsElement.atFarmOnlatitude = temp.LocationTrackerDetails[0].atFarmOnlatitude;
        readingsElement.atFarmOnlongitude = temp.LocationTrackerDetails[0].atFarmOnlongitude;
        readingsElement.puncInTime = temp.LocationTrackerDetails[0].atFarmOnDateTime;
        readingsElement.punchOutTime = temp.LocationTrackerDetails[0].atFarmOffDateTime;
        readingsElement.place = temp.LocationTrackerDetails[0].place;
        readingsElement.place_off = temp.LocationTrackerDetails[0].place_off;
        if (readingsElement.punchOutTime) {
          readingsElement.visitTime = Math.trunc(moment.duration(moment(new Date(readingsElement.punchOutTime)).diff(moment(new Date(readingsElement.puncInTime)))).asMinutes());
        }
      } else {
        readingsElement.atFarmOnlatitude = '';
        readingsElement.atFarmOnlongitude = '';
        readingsElement.puncInTime = '';
        readingsElement.punchOutTime = '';
        readingsElement.visitTime = '';
        readingsElement.place = '';
        readingsElement.place_off = '';
      }
      if (temp.FarmSiteDetails.length > 0) {
        readingsElement.customer = temp.FarmSiteDetails[0].businessID.businessName;
        readingsElement.customerId = temp.FarmSiteDetails[0].id;
      } else {
        readingsElement.customer = '';
        readingsElement.customerId = '';
      }
      if (readingsElement.customerId) {
        this.customerArray.push(readingsElement.customerId);
      }
      if (readingsElement.atFarmOnlatitude && readingsElement.atFarmOnlongitude) {
        let item: any = {
          lat: readingsElement.atFarmOnlatitude,
          long: readingsElement.atFarmOnlongitude
        };
        this.latitudeLongitudeArray.push(item);
      }
      readingsArray.push(readingsElement);
    });
    return readingsArray;
  }

  /**
   * @method getOnBoarding
   * @param {any} element - Technician record containing OnBoarding array from API response
   * @type {public}
   * @returns {Array} Array of parsed onboarding activity records
   * @description Parses OnBoarding activities with identical location/timing extraction
   * Uses activityName = "On Boarding" (with space). Note: Does not extract id field
   * 
   * @side-effect Populates latitudeLongitudeArray and customerArray
   */
  public getOnBoarding(element: any) {
    let onBoardingArray = [];
    element.OnBoarding.forEach((temp) => {
      let onBoardingElement: any = {};
      onBoardingElement.date = this.datepipe.transform(temp.date, 'dd-MMM-yyyy');
      onBoardingElement.activityName = 'On Boarding';
      onBoardingElement.place = '';
      onBoardingElement.place_off = '';
      if (temp.LocationTrackerDetails.length > 0) {
        onBoardingElement.atFarmOnlatitude = temp.LocationTrackerDetails[0].atFarmOnlatitude;
        onBoardingElement.atFarmOnlongitude = temp.LocationTrackerDetails[0].atFarmOnlongitude;
        onBoardingElement.puncInTime = temp.LocationTrackerDetails[0].atFarmOnDateTime;
        onBoardingElement.punchOutTime = temp.LocationTrackerDetails[0].atFarmOffDateTime;
        onBoardingElement.place = temp.LocationTrackerDetails[0].place;
        onBoardingElement.place_off = temp.LocationTrackerDetails[0].place_off;
        if (onBoardingElement.punchOutTime) {
          onBoardingElement.visitTime = Math.trunc(moment.duration(moment(new Date(onBoardingElement.punchOutTime)).diff(moment(new Date(onBoardingElement.puncInTime)))).asMinutes());
        }
      } else {
        onBoardingElement.atFarmOnlatitude = '';
        onBoardingElement.atFarmOnlongitude = '';
        onBoardingElement.puncInTime = '';
        onBoardingElement.punchOutTime = '';
        onBoardingElement.visitTime = '';
        onBoardingElement.place = '';
        onBoardingElement.place_off = '';
      }
      if (temp.FarmSiteDetails.length > 0) {
        onBoardingElement.customer = temp.FarmSiteDetails[0].businessID.businessName;
        onBoardingElement.customerId = temp.FarmSiteDetails[0].id;
      } else {
        onBoardingElement.customer = '';
        onBoardingElement.customerId = '';
      }
      if (onBoardingElement.customerId) {
        this.customerArray.push(onBoardingElement.customerId);
      }
      if (onBoardingElement.atFarmOnlatitude && onBoardingElement.atFarmOnlongitude) {
        let item: any = {
          lat: onBoardingElement.atFarmOnlatitude,
          long: onBoardingElement.atFarmOnlongitude
        };
        this.latitudeLongitudeArray.push(item);
      }
      onBoardingArray.push(onBoardingElement);
    });
    return onBoardingArray;
  }

  /**
   * @method arrangeSingleTechnicianTableData
   * @param {any} recordsArray - Array containing single technician's activity data from search results
   * @type {public}
   * @returns {Array} Deduplicated and organized activity records for single technician display
   * @description Consolidates activities from single technician into organized main table data
   * and calls deduplication logic
   * 
   * Process Flow:
   * 1. Extract technician name from recordsArray[0].name for display header
   * 2. Iterate through each activity type (FeedLog, ObservationLog, TreatmentLog, Sampling, HarvestLog, Readings)
   * 3. For each non-empty activity array, call getRecords() to transform and push to mainTableData
   * 4. Track activity type names in tempActivities array for filtering
   * 5. Call removeSingleTechnicianTableDuplicates() with mainTableData and tempActivities
   * 6. Returns deduplicated result with activities grouped by date/time/customer/location
   * 
   * @side-effect
   * - Sets technicianName for display
   * - Populates mainTableData with parsed activity records
   * - Calls deduplication and duplicate removal
   * - Resets mainTableData before deduplication (line: this.mainTableData = [];)
   * 
   * @example
   * // Single technician "John Doe" with multiple activity types
   * arrangeSingleTechnicianTableData([{name: 'John Doe', FeedLog: [...], ObservationLog: [...], ...}])
   * → Extracts activities → getRecords for each → Deduplicates
   * → Returns [{name: 'John Doe', date: '15-Jan-2025', activityArray: [{Feed Log}, {Observation Log}], ...}]
   */
  public arrangeSingleTechnicianTableData(recordsArray: any) {
    let record: any = {};
    let tempActivities: any = [];
    this.technicianName = recordsArray[0].name;
    record.name = recordsArray[0].name;
    record.id = recordsArray[0].id;
    record.visitRemarks = recordsArray[0].visitRemarks;
    if (recordsArray[0].FeedLog.length > 0) {
      recordsArray[0].FeedLog.forEach((item) => {
        this.getRecords(record, item);
      });
      tempActivities.push('Feed Log');
    }
    // observationlog
    if (recordsArray[0].ObservationLog.length > 0) {
      recordsArray[0].ObservationLog.forEach((item) => {
        this.getRecords(record, item);
      });
      tempActivities.push('Observation Log');
    }
    // treatment log
    if (recordsArray[0].TreatmentLog.length > 0) {
      recordsArray[0].TreatmentLog.forEach((item) => {
        this.getRecords(record, item);
      });
      tempActivities.push('Treatment Log');
    }
    // Sampling
    if (recordsArray[0].Sampling.length > 0) {
      recordsArray[0].Sampling.forEach((item) => {
        this.getRecords(record, item);
      });
      tempActivities.push('Sampling');
    }
    // HarvestLog
    if (recordsArray[0].HarvestLog.length > 0) {
      recordsArray[0].HarvestLog.forEach((item) => {
        this.getRecords(record, item);
      });
      tempActivities.push('Harvest Logs');
    }
    // Readings
    if (recordsArray[0].Readings.length > 0) {
      recordsArray[0].Readings.forEach((item) => {
        this.getRecords(record, item);
      });
      tempActivities.push('Readings');
    }
    if (this.mainTableData.length == 0) {
      this.pgLoad = true;
      this.showNoDataFlag = true;
    } else {
      this.showNoDataFlag = false;
    }
    return this.removeSingleTechnicianTableDuplicates(this.mainTableData, tempActivities);
  }

  /**
   * @method getRecords
   * @async
   * @param {any} record - Technician base record object
   * @param {any} item - Individual activity record (from getFeedLogData, getObservationLog, etc.)
   * @type {public}
   * @description Transforms activity record by adding technician context and pushes to mainTableData
   * 
   * Maps activity fields to mainTableData format:
   * - record.name → temp.name (technician name)
   * - record.id → temp.id (technician ID)
   * - item.date → temp.date (activity date)
   * - item.puncInTime → temp.punchin
   * - item.punchOutTime → temp.punchout
   * - item.customer → temp.customer (farm name)
   * - item.activityName → temp.activityName (activity type)
   * - item location/coordinates → temp latitude/longitude
   * 
   * @side-effect Appends transformed record to mainTableData array
   */
  public async getRecords(record, item) {
    let temp: any = {};
    temp.name = record.name;
    temp.id = record.id;
    temp.visitRemarks = record.visitRemarks;
    temp.date = item.date;
    temp.punchin = item.puncInTime;
    temp.punchout = item.punchOutTime;
    temp.visitTime = item.visitTime;
    temp.customer = item.customer;
    temp.customerid = item.customerId;
    temp.activityName = item.activityName;
    temp.activityId = item.id;
    temp.place = item.place;
    temp.place_off = item.place_off;
    temp.latitude = item.atFarmOnlatitude;
    temp.longitude = item.atFarmOnlongitude;
    this.mainTableData.push(temp);
  }

  /**
   * @method openMaps
   * @type {public}
   * @description Sets map visualization as active and hides other visualizations (charts)
   * 
   * @side-effect
   * - Sets isMapsClicked = true
   * - Sets isChartClicked = false
   * - Sets closeMapsCharts = true (shows maps/charts container)
   */
  public openMaps() {
    this.isMapsClicked = true;
    this.isChartClicked = false;
    this.closeMapsCharts = true;
  }

  /**
   * @method openChart
   * @type {public}
   * @description Sets chart visualization as active and hides map visualization
   * 
   * @side-effect
   * - Sets isMapsClicked = false
   * - Sets isChartClicked = true
   * - Sets closeMapsCharts = true (shows maps/charts container)
   */
  public openChart() {
    this.isMapsClicked = false;
    this.isChartClicked = true;
    this.closeMapsCharts = true;
  }

  /**
   * @method exportExcel
   * @type {public}
   * @description Exports leadsTableRecords to Excel file format
   * 
   * Calls ExcelService to export leadsTableRecords array with filename 'leads_reports_data'
   * Includes all lead metrics: conversions, area, ponds, culture type, species
   * 
   * @side-effect Triggers Excel file download via ExcelService.exportAsExcelFile()
   */
  public exportExcel() {
    this.excelService.exportAsExcelFile(this.leadsTableRecords, 'leads_reports_data');
  }

  /**
   * @method closeMapsGraph
   * @type {public}
   * @description Closes both maps and charts visualizations
   * 
   * @side-effect
   * - Sets isChartClicked = false
   * - Sets isMapsClicked = false
   * - Sets closeMapsCharts = false (hides maps/charts container)
   */
  closeMapsGraph() {
    this.isChartClicked = false;
    this.isMapsClicked = false;
    this.closeMapsCharts = false;
  }

  /**
   * @method removeSingleTechnicianTableDuplicates
   * @param {Array} mainTableRecords - All activity records for single technician
   * @param {Array} tempActivities - Array of activity type names found in data
   * @type {public}
   * @returns {Array} Deduplicated records with activities grouped by date/time/customer/location
   * @description Complex deduplication logic that consolidates multiple activities for same date/time
   * at same customer location into single records with activityArray containing all activities.
   * 
   * Deduplication Flow:
   * 1. Filter mainTableRecords by each activity type in tempActivities
   * 2. For each activity type, iterate through unique customers in customerArray
   * 3. For each customer, group records by unique dates
   * 4. For each date, check for multiple punch-in/out time combinations
   * 5. If multiple time periods exist for same date/customer:
   *    - Consolidate activities with same punch-in/out times into single record
   *    - Create activityArray containing all activities for that time period
   * 6. If single time period, directly add to result with single-item activityArray
   * 7. Sort results by date descending
   * 8. Increment totalActivities counter
   * 9. Return consolidated objArray
   * 
   * Example Consolidation:
   * Before: [{date: '15-Jan', punchin: '08:00', activity: 'Feed Log'}, 
   *          {date: '15-Jan', punchin: '08:00', activity: 'Observation Log'}]
   * After:  [{date: '15-Jan', punchin: '08:00', 
   *          activityArray: [{Feed Log}, {Observation Log}]}]
   * 
   * @side-effect
   * - Increments totalActivities
   * - Returns sorted and deduplicated data
   * - Clears mainTableData before processing
   * 
   * @example
   * removeSingleTechnicianTableDuplicates(mainTableData, ['Feed Log', 'Observation Log'])
   */
  removeSingleTechnicianTableDuplicates(mainTableRecords: any, tempActivities: any) {
    let recordArray = [];
    this.allActivitiesArray = tempActivities;
    //filter Data by Activities
    tempActivities.forEach((item) => {
      let filterArray = mainTableRecords.filter(x => x.activityName == item);
      //check activities of each customer
      this.customerArray.forEach((temp) => {
        let filterCustomerArray = filterArray.filter(x => x.customerid == temp);
        let dates = filterCustomerArray.map((value) => value.date).filter((value, index, _arr) => _arr.indexOf(value) == index);
        dates.forEach((ele) => {
          let filterDateArray = filterCustomerArray.filter(x => x.date == ele);
          if (filterDateArray.length > 1) {
            //Remove all the empty strings related to same activity when no tracking is happened
            filterDateArray = filterDateArray.filter((arr, index, self) =>
              index === self.findIndex((t) => (t.punchin === arr.punchin && t.punchout === arr.punchout)));
          }
          if (filterDateArray.length > 0) {
            recordArray = recordArray.concat(filterDateArray);
          }
        });
      });
    });
    this.mainTableData = [];
    let objArray = [];
    //sort array by date in descending order
    recordArray = recordArray.sort((a, b) => (new Date(b.date)).valueOf() - (new Date(a.date)).valueOf());
    this.totalActivities = this.totalActivities + recordArray.length;
    //Check the Mutliple activities related to same date and time of each customer
    this.customerArray.forEach((temp) => {
      let filterCustomerArray = recordArray.filter(x => x.customerid == temp);
      let dates = filterCustomerArray.map((value) => value.date).filter((value, index, _arr) => _arr.indexOf(value) == index);
      dates.forEach((ele) => {
        //checking Individual dates
        let filterDateArray = filterCustomerArray.filter(x => x.date == ele);
        if (filterDateArray.length > 1) {
          //Obtaining different times for the day the customer
          let timeArray = filterDateArray.map((item) => {
            return {punchin: item.punchin, punchout: item.punchout};
          });
          timeArray = timeArray.filter((arr, index, self) => index === self.findIndex((t) => (t.punchin === arr.punchin && t.punchout === arr.punchout)));
          if (timeArray.length > 0) {
            timeArray.forEach((timeObj) => {
              let tempArray: any = [];
              //obtaining same time and date arrays of customer
              const results = filterDateArray.filter(element => {
                return element.punchin === timeObj.punchin && element.punchout === timeObj.punchout && element.date == ele;
              });
              //Getting all the activity names related to same phase of day
              let uerArr: any = [];
              if (results.length > 0) {
                results.forEach((gen) => {
                  uerArr.push({activityName: gen.activityName, activityId: gen.activityId});
                });
              }
              let elementObj = {
                ...results[0],
                activityArray: uerArr,
              };
              tempArray.push(elementObj);
              if (tempArray.length > 0) {
                objArray = objArray.concat(tempArray);
              }
            });
          }
        } else {
          //if array consist of single data element then directly merge those records to main array
          let elementObj = {
            activityArray: [{activityName: filterDateArray[0].activityName, activityId: filterDateArray[0].activityId}],
            ...filterDateArray[0]
          };
          objArray.push(elementObj);
        }
      });
    });
    this.pgLoad = true;
    return objArray;
  }

  /**
   * @method arrangeMultipleTechnicianTableData
   * @param {Array} recordsArray - Array of technician records, each containing their activities
   * @type {public}
   * @description Processes multiple technician records by filtering out empty records and
   * delegating each technician to arrangeSingleTechnicianTableData for individual processing
   * 
   * Consolidation Flow:
   * 1. Filter out technicians with no activities across all 7 types
   * 2. For each remaining technician, wrap in array and call arrangeSingleTechnicianTableData
   * 3. Concatenate results to build dataArray
   * 4. Sort combined results by date descending
   * 5. Set pgLoad=true and show/hide data flags appropriately
   * 
   * @side-effect
   * - Populates mainTableData with combined deduplicated records from all technicians
   * - Sets loading and no-data flags
   * - Sorts by date descending
   * 
   * @example
   * // Multiple technicians with mixed activities
   * arrangeMultipleTechnicianTableData([
   *   {name: 'John', FeedLog: [...], ObservationLog: [...]},
   *   {name: 'Jane', TreatmentLog: [...], Sampling: [...]}
   * ])
   */
  arrangeMultipleTechnicianTableData(recordsArray: any) {
    let tempArray: any = [];
    let dataArray: any = [];
    recordsArray.forEach((ele) => {
      if ((ele.FeedLog.length == 0) && (ele.HarvestLog.length == 0) && (ele.ObservationLog.length == 0) && (ele.OnBoarding.length == 0)
        && (ele.Readings.length == 0) && (ele.Sampling.length == 0) && (ele.TreatmentLog.length == 0)) {
        return;
      } else {
        tempArray.push(ele);
      }
    });
    if (tempArray.length == 0) {
      this.pgLoad = true;
      this.showNoDataFlag = true;
    } else {
      tempArray.forEach((item) => {
        let data = [];
        data.push(item);
        let itemArray = this.arrangeSingleTechnicianTableData(data);
        dataArray = dataArray.concat(itemArray);
      });
      this.mainTableData = [];
      this.mainTableData = dataArray;
      this.mainTableData = this.mainTableData.sort((a, b) => (new Date(b.date)).valueOf() - (new Date(a.date)).valueOf());
      if (this.mainTableData.length >= 0) {
        this.pgLoad = true;
      }
    }
  }

  /**
   * @method allTab
   * @type {public}
   * @description Activates the "All Activities" tab view with Google Maps visualization
   * 
   * @side-effect
   * - Sets isAllTabFalg = true (shows All Activities tab)
   * - Sets isLeadsTabFlag = false
   * - Calls initializeGoogleMaps(mainTableData) to populate map with activity locations
   */
  allTab() {
    this.isAllTabFalg = true;
    this.isLeadsTabFlag = false;
    this.initializeGoogleMaps(this.mainTableData);
  }

  /**
   * @method leadsTab
   * @type {public}
   * @description Activates the "Leads" tab view for lead analytics
   * 
   * @side-effect
   * - Sets isAllTabFalg = false
   * - Sets isLeadsTabFlag = true (shows Leads tab)
   * - Sets closeMapsCharts = false (hides maps/charts container)
   */
  leadsTab() {
    this.isAllTabFalg = false;
    this.isLeadsTabFlag = true;
    this.closeMapsCharts = false;
  }

  /**
   * @method initializeGoogleMaps
   * @param {Array} activitiesRecords - Main table data containing all activities with location coordinates
   * @type {public}
   * @description Processes activity records to create Google Maps marker data with timezone-formatted timestamps
   * Filters out records without location data and transforms them for map display
   * 
   * Processing Steps:
   * 1. Clear googleMapsLatlongsPositions array
   * 2. Filter activitiesRecords to only include records with valid latitude and longitude
   * 3. For each activity, create location object with:
   *    - latitude/longitude coordinates (parsed from strings to floats)
   *    - place names (on/off locations)
   *    - dateTime (formatted with locale-specific date/time format)
   *    - farm name (customer business name)
   *    - personName (technician name)
   *    - visitTimeIn/visitTimeOut (both set to dateTime for consistency)
   * 4. Extract unique person names for technicianPersons array
   * 5. Creates googleMapsLatlongsPositions ready for map marker rendering
   * 
   * @side-effect
   * - Populates googleMapsLatlongsPositions with map-ready location objects
   * - Sets technicianPersons array with unique technician names
   * - Clears array before processing
   * 
   * @example
   * // Activity record
   * {date: '15-Jan-2025', latitude: '17.44', longitude: '78.42', customer: 'Farm ABC', ...}
   * 
   * // Map location object
   * {latitude: 17.44, longitude: 78.42, place: 'Pond 1', dateTime: 'Jan 15, 2025, 10:30 AM', farm: 'Farm ABC', ...}
   */
  initializeGoogleMaps(activitiesRecords: any) {
    this.googleMapsLatlongsPositions = [];
    const filteredActivitiesRecords = activitiesRecords.filter((record: any) => record.latitude != '' && record.longitude != '');
    if (filteredActivitiesRecords) {
      const options: any = {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        hour12: true
      };
      for (let i = 0; i < filteredActivitiesRecords.length; i++) {
        let date = new Date(filteredActivitiesRecords[i].date);
        let eachObj = {
          'latitude': filteredActivitiesRecords[i].latitude,
          'longitude': filteredActivitiesRecords[i].longitude,
          'place': filteredActivitiesRecords[i].place,
          'dateTime': date.toLocaleDateString('en-US', options),
          'farm': filteredActivitiesRecords[i].customer,
          'personName': 'Nani',
          'visitTimeIn': date.toLocaleDateString('en-US', options),
          'visitTimeOut': date.toLocaleDateString('en-US', options)
        };
        this.googleMapsLatlongsPositions.push(eachObj);
      }
      this.technicianPersons = this.googleMapsLatlongsPositions.map((obj: any) => obj.personName)
    }
  }

  /**
   * @method onMapReady
   * @param {google.maps.Map} map - Native Google Maps instance after rendering
   * @type {public}
   * @description Initializes map bounds calculation and handles marker duplication at exact coordinates
   * 
   * Initialization Process:
   * 1. Validate map reference exists, return if undefined
   * 2. Store map reference for later operations
   * 3. Create LatLngBounds object for auto-fit calculation
   * 4. Iterate through googleMapsLatlongsPositions to:
   *    - Parse latitude/longitude as floats
   *    - Validate non-NaN coordinates
   *    - Detect and offset duplicate coordinates (multiple activities same location)
   *    - Use 0.0001 degree offset when duplicates detected
   *    - Add each position to bounds
   * 5. Call fitBounds() to auto-zoom and center map on all markers
   * 6. Set zoom limit listener to prevent exceeding zoom level 20:
   *    - Add 'idle' event listener
   *    - Check zoom level after map idle
   *    - Cap zoom to 20 if exceeded
   * 
   * @side-effect
   * - Sets map reference
   * - Modifies googleMapsLatlongsPositions coordinates to offset duplicates
   * - Adjusts map bounds and zoom level
   * 
   * @example
   * // Duplicate coordinate handling
   * // Location 1: lat: 17.44, lng: 78.42
   * // Location 2: lat: 17.44, lng: 78.42 (same as Location 1)
   * // After offset: Location 2: lat: 17.4401, lng: 78.4201
   */
  onMapReady(map: google.maps.Map) {
    if (!map) {
      console.error("Google Map reference is undefined!");
      return;
    }
    this.map = map;  // Store reference to Google Map
    const bounds = new google.maps.LatLngBounds();
    const offset = 0.0001;
    const adjustedCoordinates = new Map<string, { lat: number; lng: number }>();
    this.googleMapsLatlongsPositions.forEach((location) => {
      let lat = parseFloat(location.latitude);
      let lng = parseFloat(location.longitude);
      if (isNaN(lat) || isNaN(lng)) {
        return;
      }
      let key = `${lat},${lng}`;
      while (adjustedCoordinates.has(key)) {
        lat += offset;
        lng += offset;
        key = `${lat},${lng}`;
      }
      adjustedCoordinates.set(key, {lat, lng});
      location.latitude = lat;
      location.longitude = lng;
      bounds.extend(new google.maps.LatLng(lat, lng));
    });
    this.map.googleMap?.fitBounds(bounds);
    // Limit zoom to 20
    const listener = this.map.googleMap?.addListener('idle', () => {
      if (this.map.googleMap?.getZoom()! > 20) {
        this.map.googleMap.setZoom(20);
      }
      google.maps.event.removeListener(listener!);
    });
  }

  /**
   * @method onLocationMouseOverClick
   * @param {MapMarker} marker - Google Maps marker instance
   * @param {any} locationCoordinates - Location data object for info window content
   * @type {public}
   * @description Handles map marker hover/click to display location information in info window
   * Manages info window lifecycle (close previous, open new)
   * 
   * @throws Logs error if infoWindow is undefined
   * @side-effect
   * - Sets locationTrackingInfoDetails with clicked location data
   * - Closes previously opened info window
   * - Stores reference to lastOpenInfoWindow
   * - Opens new info window on marker with setTimeout for rendering
   * 
   * @example
   * // User hovers over map marker for Farm ABC at 08:00 AM
   * onLocationMouseOverClick(marker, {latitude: 17.44, longitude: 78.42, place: 'Pond 1', ...})
   * → Sets locationTrackingInfoDetails
   * → Closes previous info window if open
   * → Opens new info window on marker with location details
   */
  public lastOpenInfoWindow: MapInfoWindow | null = null; // Declare the property

  public onLocationMouseOverClick(marker: MapMarker, locationCoordinates: any): void {
    if (!this.infoWindow) {
      console.error("Error: infoWindow is undefined.");
      return;
    }
    // Find the visit details based on location tracker ID
    this.locationTrackingInfoDetails = locationCoordinates;
    // Close the previously opened InfoWindow
    if (this.lastOpenInfoWindow) {
      try {
        this.lastOpenInfoWindow.close();
      } catch (error) {
        console.error("Error closing previous InfoWindow:", error);
      }
    }
    // Store the last opened InfoWindow
    this.lastOpenInfoWindow = this.infoWindow;
    // Open the new InfoWindow with the correct anchor (marker)
    setTimeout(() => this.infoWindow.open(marker), 0);
  }

  /**
   * @method onFiltersApplied
   * @type {public}
   * @description Master filter orchestrator for maps-level filtering by technician and/or date range
   * Determines if filtering should be applied based on selection state and triggers appropriate logic
   * 
   * Filter Logic:
   * 1. Check if either isPersonSelected or isDateRangeSelected flags are true
   * 2. If either flag is true:
   *    - Call applyFilterWithFilterType() to apply combined filters
   *    - Call openFullScreen() to display filtered map results
   * 3. If both flags are false:
   *    - Reset googleMapsLatlongsPositions to original copy (all locations)
   * 
   * @side-effect
   * - Applies or removes filters on googleMapsLatlongsPositions
   * - Triggers fullscreen map view when filters applied
   * 
   * @example
   * // User selects "John Doe" from technician dropdown
   * isPersonSelected = true
   * onFiltersApplied() → applyFilterWithFilterType() → filters map for John's visits only
   */
  public onFiltersApplied() {
    if (this.isPersonSelected || this.isDateRangeSelected) {
      this.applyFilterWithFilterType();
      this.openFullScreen();
    } else {
      this.googleMapsLatlongsPositions = this.googleMapsLatlongsPositionsCopy;
    }
  }

  /**
   * @method applyFilterWithFilterType
   * @type {private}
   * @description Applies person and/or date range filters to map positions array
   * Filters googleMapsLatlongsPositions based on selected technician and date range
   * 
   * Filtering Logic:
   * 1. If isPersonSelected: Filter positions where personName === selectedPersonName
   * 2. If date range selected and valid:
   *    - Extract startDate and endDate from gmapsDateselected
   *    - Parse visit dateTime and compare with date range
   *    - Include only positions within date range
   * 3. Return filtered array matching both conditions (if both applied)
   * 
   * @side-effect Replaces googleMapsLatlongsPositions with filtered subset
   * @example
   * // Filter for "John Doe" between 2025-01-01 and 2025-01-31
   * isPersonSelected = true, selectedPersonName = 'John Doe'
   * gmapsDateselected = {startDate: moment('2025-01-01'), endDate: moment('2025-01-31')}
   * → Returns only John's visits within date range
   */
  private applyFilterWithFilterType() {
    this.googleMapsLatlongsPositions = this.googleMapsLatlongsPositionsCopy.filter((mapsObj: any) => {
      let includeConditions = true; // Initialize shouldKeep as true
      if (this.isPersonSelected && mapsObj.personName != this.selectedPersonName) {
        includeConditions = false; // Set shouldKeep to false if condition is not met
      }
      if (this.gmapsDateselected && this.gmapsDateselected.startDate && this.gmapsDateselected.endDate) {
        this.isDateRangeSelected = true;
        const startDate = new Date(this.gmapsDateselected.startDate.format('YYYY-MM-DD'));
        const endDate = new Date(this.gmapsDateselected.endDate.format('YYYY-MM-DD'));
        const visitedDate = new Date(mapsObj.dateTime);
        if (!(visitedDate >= startDate && visitedDate <= endDate)) {
          includeConditions = false;
        }
      }
      return includeConditions;
    });
  }

  /**
   * @method openGraphImage
   * @type {public}
   * @description Conditionally shows graph/chart visualization for leads tab
   * 
   * Shows graph only when viewing leads tab (isLeadsTabFlag = true)
   * 
   * @side-effect Sets closeMapsCharts = true if in leads tab view
   */
  openGraphImage() {
    if (this.isLeadsTabFlag) {
      this.closeMapsCharts = true;
    }
  }

  /**
   * @method leadsTableData
   * @param {string} name - Technician name from current record
   * @param {Array} leads - Leads array from activity data containing conversion information
   * @type {public}
   * @description Parses leads records into displayable table format with conversion and area metrics
   * 
   * For each lead record, extracts:
   * - Basic info: type, date, technician name, farmer name, mobile, farm location
   * - Location data: state (stored as locations), pincode
   * - Culture data: culture type and species from nested culture object with fallback
   * - Area metrics: total culture area in hectares
   * - Pond count: number of ponds/sections
   * - Conversion tracking:
   *   - If type === 'Conversion': Set convertedDate and leadConvertedTime from LeadConvertedDays
   *   - Otherwise: Set to '-' (dash)
   * - Close tracking:
   *   - If type === 'Close': Set leadDeleteDate from createdAt
   *   - Otherwise: Set to '-'
   * - Notes: remarks/comments
   * 
   * @side-effect Appends parsed lead records to leadsTableRecords array
   * @example
   * // Lead record
   * {type: 'Conversion', farmerName: 'Farmer ABC', createdAt: '2025-01-15', 
   *  culture: [{name: 'Shrimp', species: [{name: 'SPF Vannamei'}]}], totalCultureArea: 5.5, ...}
   * 
   * // Parsed output
   * {type: 'Conversion', name: 'John Doe', customer: 'Farmer ABC', cultureType: 'Shrimp',
   *  species: 'SPF Vannamei', totalCultureArea: 5.5, convertedDate: '2025-01-15', ...}
   */
  leadsTableData(name: any, leads: any) {
    leads.forEach((element) => {
      let item: any = {};
      item.type = element.type;
      item.date = element.createdAt;
      item.name = name;
      item.customer = element.farmerName;
      item.mobile = element.mobileNumber;
      item.farmAt = element.farmBasedAt;
      item.pincode = element.pincode;
      // item.locations = parseInt(element.noFarmLocations);
      item.locations = element.state;
      item.cultureType = element.culture && element.culture.length > 0 && element.culture[0] && element.culture[0].name || '--';
      item.species = element.culture && element.culture.length > 0 && element.culture[0].species && element.culture[0].species.length > 0 && element.culture[0].species[0].name || '--';
      item.totalCultureArea = parseFloat(element.totalCultureArea);
      item.ponds = element.noOfPonds;
      item.type = element.type;
      if (element.type == 'Conversion') {
        item.convertedDate = element.createdAt;
        item.leadConvertedTime = element.LeadConvertedDays;
      } else {
        item.convertedDate = '-';
        item.leadConvertedTime = '-';
      }
      if (element.type == 'Close') {
        item.leadDeleteDate = element.createdAt;
      } else {
        item.leadDeleteDate = '-';
      }
      item.remarks = element.comment;
      this.leadsTableRecords.push(item);
    });
  }

  /**
   * @method arrangeLeadsTableData
   * @type {public}
   * @description Calculates aggregate lead analytics including conversion ratios, area coverage,
   * and culture type distribution
   * 
   * Analytics Calculation:
   * 1. Filter leadsTableRecords by type (Leads, Conversion, FollowUp, Close)
   * 2. Calculate totalLeads = Leads + FollowUp + Close (excluding Conversions)
   * 3. Calculate totalConversions = Conversion type count
   * 4. Calculate totalAreaofLeads = Sum of all totalCultureArea values (in hectares)
   * 5. Calculate totalLeadLocations = Unique locations by pincode count
   * 6. Culture type ratio calculations:
   *    - Count fish and shrimp culture types
   *    - If either count is 0: Set both ratios to 0
   *    - Otherwise: Calculate percentages (round to nearest integer)
   *    - shrimpRatio = (shrimp_count / total) * 100
   *    - fishRatio = (fish_count / total) * 100
   * 
   * @side-effect
   * - Sets totalLeads, totalConversions, totalAreaofLeads, totalLeadLocations
   * - Sets shrimpRatio and fishRatio percentages
   * 
   * @example
   * // Leads data
   * [{type: 'Leads', totalCultureArea: 5}, {type: 'Conversion', totalCultureArea: 3},
   *  {cultureType: 'Shrimp'}, {cultureType: 'fish'}]
   * 
   * // Results
   * totalLeads = 1, totalConversions = 1, totalAreaofLeads = 8, shrimpRatio = 50, fishRatio = 50
   */
  arrangeLeadsTableData() {
    let tempLeads = this.leadsTableRecords.filter(x => x.type === 'Leads');
    let tempConversions = this.leadsTableRecords.filter(x => x.type === 'Conversion');
    let tempFollowUp = this.leadsTableRecords.filter(x => x.type === 'FollowUp');
    let tempClose = this.leadsTableRecords.filter(x => x.type === 'Close');
    // this.totalLeads = tempConversions.length + tempFollowUp.length;
    this.totalLeads = tempLeads.length + tempFollowUp.length + tempClose.length;
    this.totalConversions = tempConversions.length;
    this.totalAreaofLeads = this.leadsTableRecords.reduce((a, b) => a + b.totalCultureArea, 0);
    // this.totalLeadLocations = this.leadsTableRecords.reduce((a, b) => a + b.locations, 0);
    this.totalLeadLocations = _.uniqBy(this.leadsTableRecords, 'pincode');
    let fish = this.leadsTableRecords.filter(x => x.cultureType === 'fish');
    let shrimp = this.leadsTableRecords.filter(x => x.cultureType === 'Shrimp');
    if (fish.length === 0 && shrimp.length === 0) {
      this.shrimpRatio = 0;
      this.fishRatio = 0;
    } else {
      this.shrimpRatio = Math.round((shrimp.length / (fish.length + shrimp.length)) * 100);
      this.fishRatio = Math.round((fish.length / (fish.length + shrimp.length)) * 100);
    }
  }

  /**
   * @lifecycle ngAfterViewInit
   * @description Angular lifecycle hook called after view initialization
   * Registers global fullscreen change event listener for responsive map dimension handling
   * 
   * @side-effect Adds 'fullscreenchange' event listener bound to onFullScreenChange handler
   */
  ngAfterViewInit() {
    // Add listener for full-screen changes
    document.addEventListener('fullscreenchange', this.onFullScreenChange.bind(this));
  }

  /**
   * @lifecycle ngOnDestroy
   * @description Angular lifecycle hook called during component destruction
   * Cleans up service subscriptions and event listeners
   * 
   * @side-effect
   * - Cancels all ongoing HTTP requests in PersonsService
   * - Removes 'fullscreenchange' event listener
   */
  ngOnDestroy() {
    this.personsService.cancelOnGoingHttpRequests();
    // Clean up event listener when component is destroyed
    document.removeEventListener('fullscreenchange', this.onFullScreenChange.bind(this));
  }

  /**
   * @method getMapNativeElement
   * @type {private}
   * @returns {HTMLElement | null} Native HTML element reference for Google Maps component
   * @description Attempts to retrieve native map element reference from ViewChild or DOM fallback
   * 
   * Retrieval Strategy:
   * 1. Check if mapElement ViewChild is available with _elementRef property
   * 2. If available, return nativeElement from _elementRef
   * 3. If not available, fallback to querySelector('agm-map') for direct DOM access
   * 4. Log warning when ViewChild not available (using fallback)
   * 5. Return null if both methods fail
   * 
   * @returns Reference to agm-map DOM element for fullscreen operations
   */
  private getMapNativeElement(): HTMLElement | null {
    if (this.mapElement && this.mapElement['_elementRef']) {
      // Check if @ViewChild reference is available
      return this.mapElement['_elementRef'].nativeElement;
    } else {
      // Fallback: use querySelector to find agm-map element directly
      console.warn('@ViewChild not available, using querySelector as a fallback.');
      return document.querySelector('agm-map') as HTMLElement;
    }
  }

  /**
   * @method openFullScreen
   * @type {public}
   * @description Initiates fullscreen mode for Google Maps visualization
   * Requests fullscreen for mapsContainer element and sets map height to 100%
   * 
   * Process:
   * 1. Get mapsContainer DOM element by ID
   * 2. Get native map element via getMapNativeElement()
   * 3. If map element exists:
   *    - Set map height to 100% via Renderer2
   *    - Call requestFullscreen() on container element
   * 4. Log error if map element not available
   * 
   * @side-effect
   * - Sets map div height to 100%
   * - Requests fullscreen mode for mapsContainer
   * - Sets isFullScreen flag via onFullScreenChange listener
   * 
   * @example
   * // User clicks fullscreen button on map
   * openFullScreen()
   * → Gets map container element
   * → Sets height to 100%
   * → Requests fullscreen mode
   * → Browser enters fullscreen with map at full viewport height
   */
  public openFullScreen(): void {
    let element = document.getElementById('mapsContainer') as HTMLElement;
    const nativeMapElement = this.getMapNativeElement();
    if (nativeMapElement) {
      this.renderer.setStyle(nativeMapElement, 'height', '100%');
      if (element.requestFullscreen) {
        element.requestFullscreen();
      }
    } else {
      console.error('mapElement is still not available in openFullScreen');
    }
  }

  /**
   * @method onFullScreenChange
   * @type {private}
   * @description Listener for fullscreen state changes to adjust map dimensions responsively
   * Called when entering or exiting fullscreen mode
   * 
   * Dimension Adjustment:
   * 1. Get native map element via getMapNativeElement()
   * 2. Check if fullscreen is currently active via !!document.fullscreenElement
   * 3. Set isFullScreen flag based on fullscreen state
   * 4. Adjust map height:
   *    - If fullscreen: height = 100% (full viewport)
   *    - If normal: height = 360px (default card height)
   * 
   * @side-effect
   * - Updates isFullScreen flag
   * - Adjusts map container height via Renderer2.setStyle()
   */
  private onFullScreenChange(): void {
    const nativeMapElement = this.getMapNativeElement();
    if (nativeMapElement) {
      // Check if full-screen mode is active
      this.isFullScreen = !!document.fullscreenElement;
      // Adjust height based on full-screen state
      this.renderer.setStyle(nativeMapElement, 'height', this.isFullScreen ? '100%' : '360px');
    }
  }

  /**
   * @method initializedatePicker
   * @type {public}
   * @description Initializes date picker with default date range (one year back to today)
   * 
   * Initialization:
   * 1. Calculate today as start of day (00:00:00)
   * 2. Calculate one year ago from today
   * 3. Patch dateRangeField with start=oneYearAgo and end=today
   * 4. Set selected object with moment objects for display
   * 5. emitEvent=false prevents change detection during patch
   * 
   * @side-effect
   * - Sets dateRangeField values to 1-year range
   * - Sets selected moment date objects
   */
  initializedatePicker(): void {
    const today = moment().startOf('day').toDate();
    const oneYearAgo = moment().subtract(1, 'year').startOf('day').toDate();
    this.dateRangeField.patchValue(
      {
        start: oneYearAgo,
        end: today
      },
      {emitEvent: false}
    );
    this.selected = {
      startDate: moment(oneYearAgo),
      endDate: moment(today)
    };
  }

  /**
   * @method onPickerClosed
   * @type {public}
   * @description Handles date picker closure event to finalize date selection
   * 
   * Closure Logic:
   * 1. Extract start and end values from dateRangeField
   * 2. If only start date selected (no end):
   *    - Patch end date to equal start date
   *    - Call onDateChanged with same start/end (single day selection)
   * 3. If both start and end dates selected:
   *    - Call onDateChanged with both dates
   * 4. Allows single-day or multi-day date range selection
   * 
   * @side-effect Calls onDateChanged() to update fromDate/toDate and sync other form controls
   */
  onPickerClosed(): void {
    const {start, end} = this.dateRangeField.value;
    if (start && !end) {
      this.dateRangeField.patchValue({end: start}, {emitEvent: false});
      this.onDateChanged({startDate: start, endDate: start});
    } else if (start && end) {
      this.onDateChanged({startDate: start, endDate: end});
    }
  }

  /**
   * @method onDateChanged
   * @param {Object} range - Date range object with startDate and endDate as Date objects
   * @type {public}
   * @description Updates component date properties when date range selection changes
   * Converts selected dates to string format for API calls
   * 
   * @side-effect
   * - Updates selected object with moment-converted dates
   * - Updates fromDate in YYYY-MM-DD format
   * - Updates toDate in YYYY-MM-DD format
   * - These formatted dates are used in searchData() API call
   * 
   * @example
   * onDateChanged({startDate: new Date('2025-01-01'), endDate: new Date('2025-01-31')})
   * → fromDate = '2025-01-01'
   * → toDate = '2025-01-31'
   * → selected = {startDate: moment('2025-01-01'), endDate: moment('2025-01-31')}
   */
  onDateChanged(range: { startDate: Date; endDate: Date }): void {
    this.selected = {
      startDate: moment(range.startDate),
      endDate: moment(range.endDate)
    };
    this.fromDate = moment(range.startDate).format('YYYY-MM-DD');
    this.toDate = moment(range.endDate).format('YYYY-MM-DD');
  }

}
